import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { formatDate } from '@utils/date';
import { invariant, nullthrows } from '@utils/invariant';
import { Breadcrumb } from 'components/Breadcrumb';
import { DataField } from 'components/DataField';
import { PageHeader } from 'components/PageHeader';
import { decimateAndFormatNumber } from '@utils/number';
import { getDisplayError } from '@utils/get-display-error';
import { FileUploaderButton } from 'src/app/document/components/FileUploaderButton';
import { ContactsDialog } from 'src/components/dialog/ContactsDialog';
import { PageHeading } from 'components/PageHeading';
import { SummaryBar } from 'components/SummaryBar';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../utils/track-event';
import { generatePaymentReminder, sendPaymentReminder, usePaymentReminder, type PaymentReminder } from '../models';
import { DelayedSpinner } from 'components/Spinner';

export type { PaymentReminder };

export const PaymentReminderPage = () => {
  const { paymentReminderId } = useParams<{ paymentReminderId: string }>();
  invariant(paymentReminderId);
  const { data, isLoading, mutate: refreshPaymentReminder } = usePaymentReminder(paymentReminderId);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  const paymentReminder = nullthrows(data, 'Betaalherinnering niet gevonden');
  const total = paymentReminder.invoices.reduce((acc, invoice) => acc + invoice.amount, 0);
  return (
    <>
      <PageHeader
        title={`Betaal herinnering ${paymentReminder.customer.name} - ${formatDate(paymentReminder.createdAt)}`}
      />

      <PageHeading
        noMarginBottom
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Betaalherinnering',
              to: '/internal/finance/payment-reminder',
            }}
            currentItem="Betaal herinnering"
          />
        }
        rightSide={
          <div className="flex gap-2">
            <div>
              <FileUploaderButton
                buttonText="RPL"
                title="Document"
                file={paymentReminder.document}
                generateDocument={async () => {
                  await generatePaymentReminder(paymentReminder.id);
                  trackEvent('payment_reminder_generated', { payment_reminder_id: paymentReminder.id });
                  // Revalidate so the generated document appears once the PDF job completes.
                  await refreshPaymentReminder();
                }}
                disableUpload
              />
            </div>

            <ContactsDialog
              triggerText="Verzend herinnering"
              title="Ben je zeker dat je de herinnering wilt versturen?"
              submitText="Verzend herinnering"
              triggerColor={paymentReminder.sentAt ? 'default' : 'primary'}
              contacts={paymentReminder.customer.contacts.map((c) => {
                return {
                  id: '' + c.id,
                  name: c.name,
                  email: c.email,
                };
              })}
              initialValue={paymentReminder.customer.contacts
                .filter((c) => c.shouldReceivePaymentReminder)
                .map((v) => '' + v.id)}
              description={`Ben je zeker dat je de betaal herinnering wilt sturen?`}
              onSubmit={async (value) => {
                try {
                  if (!value.length) {
                    throw new Error('Geen contacten geselecteerd');
                  }

                  await sendPaymentReminder(
                    paymentReminder.id,
                    value.map((v) => +v),
                  );
                  trackEvent('payment_reminder_sent', {
                    payment_reminder_id: paymentReminder.id,
                    customer_id: paymentReminder.customer.id,
                    contact_count: value.length,
                  });
                  toast.success('Betaalherinnering verstuurd');
                  // Revalidate so sentAt reflects the queued send.
                  await refreshPaymentReminder();
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon betaalherinnering niet versturen: ${getDisplayError(err)}`);
                  throw err;
                }
              }}
            />
          </div>
        }
      />

      <div className="flex flex-col gap-4 p-7">
        <SummaryBar>
          <DataField title="Klant">{paymentReminder.customer.name}</DataField>
          <DataField title="Type">{paymentReminder.reminderType}</DataField>
          <DataField title="Aangemaakt op">{formatDate(paymentReminder.createdAt)}</DataField>
          <DataField title="Verstuurd op">
            {paymentReminder.sentAt ? formatDate(paymentReminder.sentAt) : '-'}
          </DataField>
        </SummaryBar>

        <div className="card">
          <div className="grid grid-cols-2 gap-2 border-b border-border bg-surface-sunken px-5 py-2.5 text-sm font-semibold text-fg">
            <div>Factuurnummer</div>
            <div>Bedrag</div>
          </div>

          {paymentReminder.invoices.map((invoice) => {
            return (
              <div
                className="grid grid-cols-2 gap-2 border-b border-border px-5 py-2.5 text-sm"
                key={invoice.invoice.id}
              >
                <div>{invoice.invoice.invoiceNumber}</div>
                <div>{`€ ${decimateAndFormatNumber(invoice.amount, 2, {
                  decimalSeperator: ',',
                })}`}</div>
              </div>
            );
          })}

          <div className="grid grid-cols-2 gap-2 bg-surface-sunken px-5 py-2.5 text-sm font-semibold text-fg">
            <div>Totaal</div>
            <div>{`€ ${decimateAndFormatNumber(total, 2, {
              decimalSeperator: ',',
            })}`}</div>
          </div>
        </div>
      </div>
    </>
  );
};
